import React, { useMemo, useState } from "react";
import { COLORS, FONT_BODY, FONT_DISPLAY, FONT_MONO } from "../theme.js";
import { Section, StatCard, IconBadgeChip, Spinner, ErrorNote } from "../components/ui.jsx";
import Photo from "../components/Photo.jsx";
import { secondaryBtnStyle } from "../components/styles.js";
import { fmtMoney } from "../domain/format.js";
import { computeBadgeProgress, RANKS, TOP_RANK_LABEL, TOP_RANK_SUBTITLE } from "../domain/badges.js";

const FREE_TIER_LIMIT = 25;

export default function Profile({ items, displayName, profile, email, avatarUrl, onSetAvatar, onRefreshAvatar, onOpenAnalytics, onSignOut }) {
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState(null);

  async function handleAvatarFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setAvatarBusy(true);
    setAvatarError(null);
    const { error } = await onSetAvatar(file);
    if (error) setAvatarError(error);
    setAvatarBusy(false);
  }

  const totalValue = items.reduce((s, i) => s + i.value, 0);
  const brandsOwned = [...new Set(items.map((i) => i.brand))];
  const avgCondition = items.length ? Math.round(items.reduce((s, i) => s + i.condition, 0) / items.length) : 0;

  const progress = useMemo(() => computeBadgeProgress(items), [items]);
  const pct = progress.totalBadges ? progress.totalEarned / progress.totalBadges : 0;
  const nextRank = progress.allSectionsMastered
    ? null
    : RANKS.find((r) => r.min > progress.totalEarned);
  const badgesToNext = nextRank ? nextRank.min - progress.totalEarned : 0;

  const memberSince = profile?.member_since ? new Date(profile.member_since).getFullYear() : new Date().getFullYear();
  const tier = profile?.tier || "Free";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <label style={{ position: "relative", cursor: avatarBusy ? "wait" : "pointer", flex: "none" }}>
          <Photo
            src={avatarUrl}
            onExpired={onRefreshAvatar}
            style={{ width: 60, height: 60, borderRadius: "50%", objectFit: "cover", border: `1px solid ${COLORS.gold}`, display: "block" }}
            fallback={
              <div
                style={{
                  width: 60, height: 60, borderRadius: "50%",
                  background: `linear-gradient(145deg, ${COLORS.goldBright}, ${COLORS.gold})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: FONT_DISPLAY, fontSize: 22, color: "#1A1408",
                }}
              >
                {(displayName || "C").slice(0, 1).toUpperCase()}
              </div>
            }
          />
          <div
            style={{
              position: "absolute", bottom: -2, right: -2, width: 20, height: 20, borderRadius: "50%",
              background: COLORS.blackCard2, border: `1px solid ${COLORS.gold}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, color: COLORS.goldBright,
            }}
          >
            {avatarBusy ? <Spinner size={10} /> : "+"}
          </div>
          <input type="file" accept="image/*" disabled={avatarBusy} onChange={handleAvatarFile} style={{ display: "none" }} />
        </label>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: 19, color: COLORS.cream }}>Collector's Passport</div>
          <div style={{ fontSize: 12, color: COLORS.creamFaint, fontFamily: FONT_BODY }}>
            Member since {memberSince} · {tier} tier
          </div>
          {email && <div style={{ fontSize: 11.5, color: COLORS.creamFaint, fontFamily: FONT_BODY, marginTop: 2 }}>{email}</div>}
        </div>
      </div>

      {/* Every other photo in this app is private. This one is not, and saying
          so before someone picks a face is the only honest place to say it. */}
      <div style={{ fontSize: 11, color: COLORS.creamFaint, fontFamily: FONT_BODY, lineHeight: 1.5, marginTop: -12 }}>
        Your profile photo is shown to other collectors in Baggage Claim. Your items and their photos stay private.
      </div>

      <ErrorNote message={avatarError} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <StatCard label="Total items" value={`${items.length} / ${FREE_TIER_LIMIT}`} />
        <StatCard label="Est. collection value" value={fmtMoney(totalValue)} accent={COLORS.goldBright} />
        <StatCard label="Favorite brands" value={brandsOwned.slice(0, 2).join(", ") || "—"} />
        <StatCard label="Preservation score" value={`${avgCondition}%`} />
      </div>

      <button onClick={onOpenAnalytics} style={secondaryBtnStyle}>
        View Collection Analytics →
      </button>

      <Section title="Rank">
        <div
          style={{
            background: progress.allSectionsMastered ? "rgba(232,199,126,0.10)" : COLORS.blackCard,
            border: `1px solid ${progress.allSectionsMastered ? COLORS.goldBright : COLORS.gold}`,
            borderRadius: 14,
            padding: 16,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
            <span style={{ fontFamily: FONT_DISPLAY, fontSize: 20, color: progress.allSectionsMastered ? COLORS.goldBright : COLORS.cream }}>
              {progress.allSectionsMastered ? "🎖️ " : ""}{progress.rank}
            </span>
            <span style={{ fontSize: 11.5, fontFamily: FONT_MONO, color: COLORS.goldBright }}>
              {progress.totalEarned} / {progress.totalBadges} badges
            </span>
          </div>

          {progress.allSectionsMastered ? (
            <div style={{ fontSize: 12, color: COLORS.creamDim, fontFamily: FONT_BODY, fontStyle: "italic" }}>
              {TOP_RANK_SUBTITLE}
            </div>
          ) : (
            <>
              <div style={{ height: 6, borderRadius: 4, background: COLORS.lineFaint, overflow: "hidden", marginBottom: 8 }}>
                <div style={{ height: "100%", width: `${Math.round(pct * 100)}%`, background: `linear-gradient(90deg, ${COLORS.gold}, ${COLORS.goldBright})` }} />
              </div>
              {nextRank && (
                <div style={{ fontSize: 11.5, color: COLORS.creamFaint, fontFamily: FONT_BODY }}>
                  {badgesToNext} more badge{badgesToNext === 1 ? "" : "s"} to reach {nextRank.label}
                </div>
              )}
              {/* Said plainly, because a ladder that looks like a counter invites
                  farming the easiest section to the top. */}
              <div style={{ fontSize: 11, color: COLORS.creamFaint, fontFamily: FONT_BODY, marginTop: 4 }}>
                {TOP_RANK_LABEL} is earned by mastering every section below — not by badge count alone.
              </div>
            </>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 12 }}>
          {[...RANKS, { label: TOP_RANK_LABEL, isTop: true }].map((r) => {
            const reached = r.isTop ? progress.allSectionsMastered : progress.totalEarned >= r.min;
            const current = r.label === progress.rank;
            return (
              <div
                key={r.label}
                style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "9px 12px", borderRadius: 8,
                  background: current ? "rgba(201,162,39,0.13)" : "transparent",
                  border: `1px solid ${current ? COLORS.gold : COLORS.lineFaint}`,
                  opacity: reached ? 1 : 0.45,
                }}
              >
                <span style={{ fontSize: 13, fontFamily: FONT_BODY, color: reached ? COLORS.cream : COLORS.creamFaint, fontWeight: current ? 700 : 400 }}>
                  {r.isTop ? "🎖️ " : ""}{r.label}
                </span>
                <span style={{ fontSize: 10.5, fontFamily: FONT_MONO, color: COLORS.creamFaint }}>
                  {reached ? (current ? "Current" : "Reached") : r.isTop ? "Locked" : `${r.min} badges`}
                </span>
              </div>
            );
          })}
        </div>
      </Section>

      {progress.sections.map((section) => (
        <Section
          key={section.key}
          title={`${section.icon} ${section.title}${section.mastered ? " · Mastered" : ""}`}
          right={
            <span style={{ fontSize: 10.5, fontFamily: FONT_MONO, color: COLORS.creamFaint }}>
              {section.earnedCount}/{section.totalBadges}
            </span>
          }
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            {section.badges.map((b) => (
              <IconBadgeChip key={b.key} icon={b.icon} label={b.label} earned={section.earnedKeys.has(b.key)} />
            ))}
          </div>
        </Section>
      ))}

      <button onClick={onSignOut} style={{ ...secondaryBtnStyle, borderColor: COLORS.lineFaint, color: COLORS.creamDim }}>
        Sign out
      </button>

      <div style={{ fontSize: 10.5, fontFamily: FONT_BODY, color: COLORS.creamFaint, lineHeight: 1.6, textAlign: "center" }}>
        SENOGARA is an independent collection-management tool. It is not affiliated with, endorsed
        by, or sponsored by any brand catalogued within it. All brand names and trademarks belong to
        their respective owners.
      </div>

      {/* Which build am I actually looking at? Answers the cached-bundle question. */}
      <div style={{ fontSize: 10, fontFamily: FONT_MONO, color: COLORS.creamFaint, textAlign: "center", opacity: 0.7, paddingBottom: 8 }}>
        build {__BUILD_ID__} · {__BUILD_TIME__}
      </div>
    </div>
  );
}
